// src/v2/pulse/LeaseExposurePane.jsx
//
// PULSE-LEASE-EXPOSURE-S1 — "Lease exposure" web part for the Pulse home
// Row 3 slot (replaces the "Coming soon" placeholder).
//
// Compact pane: header + info tooltip + basis toggle, total-exposure tinted
// block (left column top), 3-across horizon tiles below it, committed-spend-
// by-year bar chart (right column, fills height), decision line deep-linking
// to the Leases action view.
//
// Expand ("More ▾"): gross→net→PV bridge, market concentration, committed
// vs. under-review strategy split, top 3 leases, currency/methodology note.
//
// Both cost bases are fetched for the selected location scope and kept in
// memory; switching basis selects the already-loaded server result.
//
// No lease dollar figure is ever passed to console.*/logger from this file.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { T } from "../../app/tokens.js";
import { PaneInfoTooltip } from "./PaneInfoTooltip.jsx";
import { getPulseLeaseExposure } from "../../data/pulseClient.js";
import { getCostLeakageReport } from "../../data/reportsClient.js";
import { PULSE_PANE_H, OUTBOUND_LINK_STYLE } from "./pulseConstants.js";
import { LocationScopePicker, OfficeLocationLabel } from "./LocationScopePicker.jsx";
import { ToolbarViewToggle } from "../components/ToolbarViewToggle.jsx";
import { useCountUp } from "../hooks/useCountUp.js";
import { useDrawIn } from "../hooks/useDrawIn.js";
import { LEASE_DECISION_WINDOW_SCOPE } from "../../shared/leaseDecisionWindow.js";
import { useFxConversionBasis, formatFxBasisSummary } from "../../data/useFxConversionBasis.js";
import { useChartHoverIntent } from "../../components/useChartHoverIntent.js";

function fmtUsd(n, opts = {}) {
  if (n == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(opts.precise ? 2 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`;
  return `${sign}$${Math.round(abs).toLocaleString()}`;
}

function fmtPct(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `${Math.round(n)}%`;
}

const SCOPE_OPTIONS = [
  { key: "base", label: "Base", testid: "v2-lease-exposure-scope-base" },
  { key: "fully_loaded", label: "Fully-loaded", testid: "v2-lease-exposure-scope-fully_loaded" },
];

function TotalBlock({ value, leaseCount, scopeLabel, rateLabel, onScopeChange, scope }) {
  return (
    <div data-testid="v2-lease-exposure-total-cell">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div style={{
          fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid,
          textTransform: "uppercase", letterSpacing: ".06em",
        }}>
          Total exposure
        </div>
        <ToolbarViewToggle
          ariaLabel="Lease exposure cost basis"
          testId="v2-lease-exposure-scope"
          segments={SCOPE_OPTIONS}
          value={scope}
          onChange={onScopeChange}
          compact
        />
      </div>
      <div style={{
        fontSize: T.fsXl, fontWeight: T.fwDisplay, color: "var(--v2-dark)",
        letterSpacing: "-.02em", lineHeight: 1, marginTop: 5,
      }}>
        {fmtUsd(value)}
      </div>
      <div
        data-testid="v2-lease-exposure-caption"
        style={{ fontSize: T.fsXs, color: "var(--v2-mid)", fontWeight: T.fwBold, marginTop: 7, flexWrap: "wrap", lineHeight: 1.4 }}
      >
        All-in committed · {leaseCount} lease{leaseCount === 1 ? "" : "s"} · {scopeLabel} · {rateLabel}
      </div>
    </div>
  );
}

const HORIZON_TOOLTIP = "Rolling from today; partial years pro-rated by month.";

function MetricCell({ label, value, testId, tooltip }) {
  return (
    <div
      data-testid={testId}
      title={tooltip}
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        background: T.bg,
        padding: "5px 11px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, color: "var(--v2-dark)", letterSpacing: "-.02em" }}>
        {fmtUsd(value)}
      </div>
      <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid, marginTop: 2, textTransform: "uppercase", letterSpacing: ".04em" }}>
        {label}
      </div>
    </div>
  );
}

function StrategySplitBar({ split }) {
  const committed = Number(split?.committedUsd) || 0;
  const underReview = Number(split?.underReviewUsd) || 0;
  const total = committed + underReview;
  const committedPct = total > 0 ? (committed / total) * 100 : 0;
  const underReviewPct = total > 0 ? (underReview / total) * 100 : 0;

  return (
    <div data-testid="v2-lease-exposure-strategy-split" style={{ marginTop: 14 }}>
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        gap: 8, marginBottom: 5,
      }}>
        <span style={{
          fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid,
          textTransform: "uppercase", letterSpacing: ".05em",
        }}>
          Committed vs. under review
        </span>
        <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)", whiteSpace: "nowrap" }}>
          {fmtUsd(total)}
        </span>
      </div>
      <div
        role="img"
        aria-label={`${fmtUsd(committed)} committed, ${fmtUsd(underReview)} under review`}
        style={{
          display: "flex", height: 7, overflow: "hidden", borderRadius: 999,
          background: "var(--v2-hair)",
        }}
      >
        {committedPct > 0 && (
          <span
            data-testid="v2-lease-exposure-split-committed"
            style={{ width: `${committedPct}%`, background: "var(--v2-indigo-bar)" }}
          />
        )}
        {underReviewPct > 0 && (
          <span
            data-testid="v2-lease-exposure-split-under-review"
            style={{ width: `${underReviewPct}%`, background: "var(--v2-amber)" }}
          />
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 5 }}>
        <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>
          <i style={{ display: "inline-block", width: 6, height: 6, borderRadius: 2, background: "var(--v2-indigo-bar)", marginRight: 4 }} />
          {fmtUsd(committed)} committed
        </span>
        <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)", textAlign: "right" }}>
          {fmtUsd(underReview)} under review
          <i style={{ display: "inline-block", width: 6, height: 6, borderRadius: 2, background: "var(--v2-amber)", marginLeft: 4 }} />
        </span>
      </div>
    </div>
  );
}

export function BarTooltip({ y, fmtUsd, visible }) {
  const tooltipRef = useRef(null);
  const appliedOffset = useRef(0);
  const [offset, setOffset] = useState(0);
  const isDecision = y.decision > 0;
  const label = isDecision ? "in a decision window" : "committed";
  const total = y.base + y.decision;
  const yearLabel = typeof y.year === "number" ? `'${String(y.year).slice(-2)}` : y.year;
  const content = `${yearLabel} · ${fmtUsd(total)} · ${label}`;
  useLayoutEffect(() => {
    if (!visible || !tooltipRef.current) return;
    const node = tooltipRef.current;
    const chart = node.closest('[data-testid="v2-lease-exposure-chart"]');
    const measure = () => {
      const bounds = chart.getBoundingClientRect();
      const box = node.getBoundingClientRect();
      // Measure the uncorrected box; the applied transform is not an invalidation.
      const left = box.left - appliedOffset.current;
      const right = box.right - appliedOffset.current;
      const next = Math.max(bounds.left - left, Math.min(0, bounds.right - right));
      if (Math.abs(next - appliedOffset.current) > 0.5) {
        appliedOffset.current = next;
        setOffset(next);
      }
    };
    measure();
    // ResizeObserver tracks size, not our transform, so it cannot re-arm on offset.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    observer.observe(chart);
    return () => observer.disconnect();
  }, [visible, content]);
  return (
    <div ref={tooltipRef} role="tooltip" id={`lease-year-tip-${y.year}`} data-testid={`v2-lease-exposure-tooltip-year-${y.year}`} style={{
      position: "absolute",
      bottom: "calc(100% + 8px)",
      left: "50%",
      transform: `translateX(calc(-50% + ${offset}px))`,
      display: visible ? "block" : "none",
      maxWidth: "100cqi",
      width: "max-content",
      background: "var(--v2-tooltip-bg)",
      color: "var(--v2-card)",
      borderRadius: 8,
       fontSize: T.tooltip.fontSize,
       fontWeight: T.tooltip.fontWeight,
       lineHeight: T.tooltip.lineHeight,
      padding: "4px 8px",
      whiteSpace: "normal",
      boxShadow: "0 4px 12px rgba(16,36,58,.24)",
      pointerEvents: "none",
      zIndex: 10,
      opacity: visible ? 1 : 0,
      transition: "opacity 0.15s ease",
    }}>
      {content}
      <div style={{
        position: "absolute",
        top: "100%",
        left: `calc(50% - ${offset}px)`,
        transform: "translateX(-50%)",
        width: 0,
        height: 0,
        borderLeft: "5px solid transparent",
        borderRight: "5px solid transparent",
        borderTop: "5px solid var(--v2-tooltip-bg)",
      }} />
    </div>
  );
}

// ByYearBarChart renders a stacked bar per year:
//   • bottom segment (base amount)     → indigo  (committed pass-through)
//   • top segment    (decision amount) → amber   (lease ends/breaks that year)
// This ensures committed spend is always indigo and decision-window spend is
// always amber, regardless of whether both are present in the same year.
export function ByYearBarChart({ years, resetKey }) {
  const [hoveredYear, setHoveredYear] = useState(null);
  const intent = useChartHoverIntent(JSON.stringify([resetKey, years]));

  if (!years || years.length === 0) {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center", flex: 1,
        color: "var(--v2-light)", fontSize: T.fsBase, fontStyle: "italic",
      }}>
        No committed lease spend data
      </div>
    );
  }
  const VISIBLE_YEARS = 6;
  const shownYears = years.slice(0, VISIBLE_YEARS);
  const beyondYears = years.slice(VISIBLE_YEARS);
  const beyondBucket = beyondYears.length > 0
    ? {
        year: "+",
        base: beyondYears.reduce((s, y) => s + y.base, 0),
        decision: beyondYears.reduce((s, y) => s + y.decision, 0),
      }
    : null;
  const bars = beyondBucket ? [...shownYears, beyondBucket] : shownYears;
  const maxYearlyTotal = Math.max(...shownYears.map((y) => y.base + y.decision), 1);
  const YEARLY_WITH_TAIL_MAX_PCT = 78;
  const TAIL_CAP_PCT = 88;

  return (
    <div {...intent.containerProps} className="v2-lease-exposure-chart" data-testid="v2-lease-exposure-chart">
      {/* Legend row above bars */}
      <div data-testid="v2-lease-exposure-legend" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "4px 10px", flexWrap: "wrap", minWidth: 0, marginBottom: 4 }}>
        <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid, textTransform: "uppercase", letterSpacing: ".04em" }}>BY YEAR</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", minWidth: 0, fontSize: T.fsXs, color: "var(--v2-mid)" }}>
          <span><span style={{ display: "inline-block", width: 7, height: 7, borderRadius: 2, background: "var(--v2-indigo-bar)", marginRight: 3 }} />Committed</span>
          <span><span style={{ display: "inline-block", width: 7, height: 7, borderRadius: 2, background: "var(--v2-amber)", marginRight: 3 }} />In a decision window</span>
        </div>
      </div>
      {/* Bar strip — fills remaining column height */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flex: 1, minWidth: 0, minHeight: 0 }}>
        {bars.map((y) => {
          const total = y.base + y.decision;
          const isTail = y.year === "+";
          const proportionalPct = (total / maxYearlyTotal) * (beyondBucket ? YEARLY_WITH_TAIL_MAX_PCT : 100);
          const isTailCapped = isTail && proportionalPct > TAIL_CAP_PCT;
          const totalPct = isTail ? Math.min(proportionalPct, TAIL_CAP_PCT) : proportionalPct;
          const basePct  = total > 0 ? (y.base     / total) * 100 : 0;
          const decPct   = total > 0 ? (y.decision / total) * 100 : 0;
          const isHovered = hoveredYear === y.year;
          const pointIntentProps = intent.pointProps(total > 0 ? y.year : null);
          return (
            <div
              key={y.year}
              data-testid={`v2-lease-exposure-bar-${y.year}`}
              {...pointIntentProps}
              role="group"
              aria-label={`${y.year} lease exposure`}
              aria-describedby={intent.open && intent.point === y.year ? `lease-year-tip-${y.year}` : undefined}
              style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, height: "100%", position: "relative", cursor: "help" }}
              onMouseEnter={() => { setHoveredYear(y.year); pointIntentProps.onMouseEnter(); }}
              onMouseLeave={(event) => { setHoveredYear(null); pointIntentProps.onMouseLeave(event); }}
            >
              {total > 0 && (
                <BarTooltip y={y} fmtUsd={fmtUsd} visible={intent.open && intent.point === y.year} />
              )}
              <div style={{ width: "66%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", position: "relative" }}>
                {isTailCapped && (
                  <div
                    data-testid="v2-lease-exposure-tail-value"
                    style={{
                      position: "absolute",
                      bottom: `calc(${TAIL_CAP_PCT}% + 3px)`,
                      right: 0,
                      color: "var(--v2-dark)",
                      fontSize: T.fsXs,
                      fontWeight: T.fwDisplay,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtUsd(total)}
                  </div>
                )}
                {/* Stacked bar: decision (amber) on top, base (indigo) on bottom */}
                <div data-testid={`v2-lease-exposure-stack-${y.year}`} style={{
                  width: "100%",
                  height: `${totalPct}%`,
                  minHeight: total > 0 ? 2 : 0,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "3px 3px 0 0",
                  overflow: "hidden",
                  filter: isHovered ? "brightness(0.92)" : "none",
                  transition: "filter 0.15s ease",
                  position: "relative",
                }}>
                  {decPct > 0 && (
                    <div data-testid={`v2-lease-exposure-decision-${y.year}`} style={{ width: "100%", height: `${decPct}%`, background: "var(--v2-amber)", flexShrink: 0 }} />
                  )}
                  {basePct > 0 && (
                    <div data-testid={`v2-lease-exposure-base-${y.year}`} style={{ width: "100%", flex: 1, background: "var(--v2-indigo-bar)" }} />
                  )}
                  {isTailCapped && (
                    <span
                      data-testid="v2-lease-exposure-tail-break"
                      role="img"
                      aria-label="Value exceeds chart scale"
                      style={{
                        position: "absolute",
                        top: 10,
                        left: -2,
                        right: -2,
                        height: 7,
                        background:
                          "linear-gradient(135deg, transparent 0 36%, var(--v2-card) 37% 49%, transparent 50% 61%, var(--v2-card) 62% 74%, transparent 75%)",
                      }}
                    />
                  )}
                </div>
              </div>
              <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", flexShrink: 0 }}>{typeof y.year === "number" ? `'${String(y.year).slice(-2)}` : y.year}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function reportUsd(value) {
  const amount = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (amount >= 1e6) return `${sign}$${(amount / 1e6).toFixed(1)}M`;
  if (amount >= 1e3) return `${sign}$${(amount / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(amount)}`;
}

function SlabHeading({ children }) {
  return (
    <div style={{
      fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em",
      textTransform: "uppercase", color: T.mid, marginBottom: 8,
    }}>
      {children}
    </div>
  );
}

const MARKET_COLORS = [T.dark, T.blue, T.tourBlue, T.barTintBorder, T.blueSoftBorder];
const DETAIL_COUNT_UP_DURATION = 1400;
const CONCENTRATION_DRAW_DURATION = 750;

function FormattedNumber({ value, format, fallback = "Not available" }) {
  const numberValue = value != null && Number.isFinite(Number(value)) ? Number(value) : null;
  return <>{numberValue == null ? fallback : format(numberValue)}</>;
}

function CountUpNumber({ value, format, fallback = "Not available" }) {
  const numberValue = value != null && Number.isFinite(Number(value)) ? Number(value) : null;
  const displayed = useCountUp(numberValue ?? 0, numberValue == null ? "unavailable" : numberValue, {
    from: 0,
    duration: DETAIL_COUNT_UP_DURATION,
  });
  return <>{numberValue == null ? fallback : format(displayed)}</>;
}

function ConcentrationRingView({
  segments,
  topTwoShare,
  selectedMarket,
  hoveredMarketId,
  circumference,
  progress,
  activateMarket,
  clearMarket,
}) {
  return (
    <div
      data-testid="v2-lease-exposure-concentration-ring"
      role="group"
      aria-label="Market concentration and lease drivers"
      style={{ width: 142, height: 142, position: "relative", flex: "0 0 auto" }}
    >
      <svg
        data-testid="v2-lease-exposure-market-donut"
        width="142"
        height="142"
        viewBox="0 0 142 142"
        aria-label={`Top two markets are ${fmtPct(topTwoShare)} of gross exposure`}
        style={{ display: "block", transform: "rotate(-90deg)", overflow: "visible" }}
      >
        <circle cx="71" cy="71" r="53" fill="none" stroke="var(--v2-hair)" strokeWidth="22" />
        {segments.map((segment) => {
          const isSelected = hoveredMarketId === segment.marketId;
          const isDimmed = hoveredMarketId != null && !isSelected;
          const dashLength = circumference * segment.share / 100 * progress;
          return (
            <circle
              key={segment.marketId}
              data-testid={`v2-lease-exposure-market-arc-${segment.marketId}`}
              data-market-id={segment.marketId}
              role="button"
              tabIndex={0}
              aria-label={`${fmtPct(segment.share)} ${segment.marketName}`}
              onMouseEnter={() => activateMarket(segment.marketId)}
              onMouseLeave={clearMarket}
              onFocus={() => activateMarket(segment.marketId)}
              onBlur={clearMarket}
              cx="71"
              cy="71"
              r="53"
              fill="none"
              stroke={segment.color}
              strokeWidth={isSelected ? 26 : 22}
              strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={-circumference * segment.offset / 100}
              opacity={isDimmed ? 0.28 : 1}
              style={{ cursor: "pointer", transition: "stroke-width .15s ease, opacity .15s ease" }}
            />
          );
        })}
      </svg>
      <div
        data-testid="v2-lease-exposure-ring-center"
        aria-live="polite"
        style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center", pointerEvents: "none" }}
      >
        <div>
          <div data-testid="v2-lease-exposure-ring-center-value" style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, color: "var(--v2-dark)", lineHeight: 1 }}>
            {selectedMarket ? fmtPct(selectedMarket.share) : fmtPct(topTwoShare)}
          </div>
          <div data-testid="v2-lease-exposure-ring-center-label" style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid, marginTop: 4, lineHeight: 1.3, maxWidth: 66, textTransform: "uppercase", letterSpacing: ".04em" }}>
            {selectedMarket ? selectedMarket.marketName : <>IN TOP 2<br />MARKETS</>}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnimatedConcentrationRing(props) {
  const progress = useDrawIn(0, { duration: CONCENTRATION_DRAW_DURATION });
  return <ConcentrationRingView {...props} progress={progress} />;
}

function ConcentrationDrivers({ markets, exposure }) {
  const listedShare = markets.reduce((sum, market) => sum + (Number(market.pct) || 0), 0);
  const topTwoShare = markets.slice(0, 2).reduce((sum, market) => sum + (Number(market.pct) || 0), 0);
  const largestMarket = markets[0];
  const [hoveredMarketId, setHoveredMarketId] = useState(null);
  let offset = 0;
  const segments = markets.map((market, index) => {
    const share = Math.max(0, Math.min(100, Number(market.pct) || 0));
    const segment = { ...market, share, color: MARKET_COLORS[index % MARKET_COLORS.length], offset };
    offset += share;
    return segment;
  });
  const otherShare = markets.length
    ? Math.max(0, 100 - Math.min(100, listedShare))
    : 0;
  if (otherShare > 0) {
    segments.push({
      marketId: "__other__",
      marketName: "Other markets",
      share: otherShare,
      color: T.utilizationSparkAlt,
      offset,
    });
  }
  const colorByMarketId = new Map(segments.map((market) => [market.marketId, market.color]));
  const drivers = exposure.leaseDrivers ?? [];
  const totalTopThree = exposure.topThreeExposureUsd;
  const sharePct = exposure.topThreeExposurePct;
  const circumference = 2 * Math.PI * 53;
  const selectedMarket = segments.find((market) => market.marketId === hoveredMarketId);

  const activateMarket = (marketId) => setHoveredMarketId(marketId);
  const clearMarket = () => setHoveredMarketId(null);

  return (
    <div data-testid="v2-lease-exposure-concentration-drivers">
      <SlabHeading>Concentration &amp; drivers</SlabHeading>
      <div style={{ display: "flex", alignItems: "center", gap: 16, minWidth: 0 }}>
        <div style={{ flex: "0 0 auto" }}>
          {segments.length ? (
            <AnimatedConcentrationRing
              key="market-data-ready"
              segments={segments}
              topTwoShare={topTwoShare}
              selectedMarket={selectedMarket}
              hoveredMarketId={hoveredMarketId}
              circumference={circumference}
              activateMarket={activateMarket}
              clearMarket={clearMarket}
            />
          ) : (
            <ConcentrationRingView
              segments={segments}
              topTwoShare={topTwoShare}
              selectedMarket={selectedMarket}
              hoveredMarketId={hoveredMarketId}
              circumference={circumference}
              progress={1}
              activateMarket={activateMarket}
              clearMarket={clearMarket}
            />
          )}
        </div>
        <div data-testid="v2-lease-exposure-driver-list" style={{ flex: 1, minWidth: 0 }}>
          {drivers.length ? drivers.map((lease) => (
            <a
              key={lease.leaseId}
              data-testid={`v2-lease-exposure-driver-${lease.leaseId}`}
              href={`?actionNav=lease&actionNavId=${encodeURIComponent(lease.leaseId)}&actionNavLocId=${encodeURIComponent(lease.locationId)}`}
              onMouseEnter={() => activateMarket(lease.marketId)}
              onMouseLeave={clearMarket}
              onFocus={() => activateMarket(lease.marketId)}
              onBlur={clearMarket}
              style={{
                display: "flex", alignItems: "center", gap: 7, padding: "5px 4px",
                borderTop: "1px solid var(--v2-hair)", borderRadius: 6,
                background: hoveredMarketId === lease.marketId ? "var(--v2-blue-tint)" : "transparent",
                opacity: hoveredMarketId != null && hoveredMarketId !== lease.marketId ? 0.4 : 1,
                color: "var(--v2-dark)", textDecoration: "none", minWidth: 0,
                transition: "background .15s ease, opacity .15s ease",
              }}
            >
              <span aria-hidden="true" data-testid={`v2-lease-exposure-driver-dot-${lease.leaseId}`} style={{ width: 9, height: 9, borderRadius: 2, flex: "0 0 auto", background: colorByMarketId.get(lease.marketId) || T.utilizationSparkAlt }} />
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: T.fsXs }}>
                <strong data-testid={`v2-lease-exposure-driver-name-${lease.leaseId}`} style={{ fontWeight: T.fwBold }}>{lease.locationAlias || lease.marketName || lease.leaseId}</strong>
                <span data-testid={`v2-lease-exposure-driver-market-${lease.leaseId}`} style={{ color: "var(--v2-mid)", fontSize: T.fsXs, fontWeight: T.fwSemibold }}> · {lease.marketName || "Unassigned"}</span>
              </span>
              <strong data-testid={`v2-lease-exposure-driver-amount-${lease.leaseId}`} style={{ marginLeft: "auto", paddingLeft: 5, whiteSpace: "nowrap", fontSize: T.fsXs, fontWeight: T.fwDisplay, fontVariantNumeric: "tabular-nums" }}>
                <FormattedNumber value={lease.remainingUsd} format={fmtUsd} fallback={fmtUsd(lease.remainingUsd)} />
              </strong>
            </a>
          )) : <div style={{ fontSize: T.fsXs, color: "var(--v2-light)" }}>Not available</div>}
          {exposure.otherLeaseCount > 0 && (
            <div
              data-testid="v2-lease-exposure-driver-other"
              onMouseEnter={() => activateMarket("__other__")}
              onMouseLeave={clearMarket}
              style={{
                display: "flex", alignItems: "center", gap: 7, padding: "5px 4px",
                borderTop: "1px solid var(--v2-hair)", borderRadius: 6, fontSize: T.fsXs,
                background: hoveredMarketId === "__other__" ? "var(--v2-blue-tint)" : "transparent",
                opacity: hoveredMarketId != null && hoveredMarketId !== "__other__" ? 0.4 : 1,
                transition: "background .15s ease, opacity .15s ease",
              }}
            >
              <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 2, flex: "0 0 auto", background: colorByMarketId.get("__other__") || T.utilizationSparkAlt }} />
              <strong><FormattedNumber value={exposure.otherLeaseCount} format={(value) => Math.round(value).toLocaleString()} /> other lease{exposure.otherLeaseCount === 1 ? "" : "s"}</strong>
              <strong style={{ marginLeft: "auto", whiteSpace: "nowrap", fontWeight: T.fwDisplay, fontVariantNumeric: "tabular-nums" }}>
                <FormattedNumber value={exposure.otherLeaseExposureUsd} format={fmtUsd} fallback={fmtUsd(exposure.otherLeaseExposureUsd)} />
              </strong>
            </div>
          )}
        </div>
      </div>
      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        {largestMarket ? (
        <div data-testid="v2-lease-exposure-concentration-callout" role="note" style={{ fontSize: T.fsXs, color: "var(--v2-mid)", lineHeight: 1.4 }}>
            <strong style={{ color: "var(--v2-amber)" }}>{largestMarket.marketName} is <FormattedNumber value={largestMarket.pct} format={fmtPct} fallback={fmtPct(largestMarket.pct)} /></strong>
            {" "}of exposure - high single-market concentration.
          </div>
        ) : <span style={{ fontSize: T.fsXs, color: "var(--v2-light)" }}>Market concentration not available</span>}
        <div data-testid="v2-lease-exposure-top-three" style={{ fontSize: T.fsXs, color: "var(--v2-mid)", whiteSpace: "nowrap" }}>
          Top 3 leases = <strong style={{ color: "var(--v2-dark)" }}>
            {totalTopThree == null
              ? "Not available"
              : <><FormattedNumber value={totalTopThree} format={fmtUsd} /> (<FormattedNumber value={sharePct} format={fmtPct} fallback="—" />)</>}
          </strong>
        </div>
      </div>
    </div>
  );
}

function ConcessionRow({ label, value, highlight = false, loading = false, partial = false }) {
  const fallback = loading ? "Loading…" : "Not available";
  return (
    <div
      data-testid={`v2-lease-exposure-concession-${label.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
      data-partial={partial ? "true" : "false"}
      style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--v2-hair)" }}
    >
      <div data-testid="v2-lease-exposure-concession-label" style={{ fontSize: T.fsXs, color: "var(--v2-mid)", minWidth: 0 }}>
        {label}
      </div>
      <div data-testid="v2-lease-exposure-concession-value" style={{ fontSize: T.fsXs, fontWeight: T.fwDisplay, color: highlight ? "var(--v2-green)" : "var(--v2-dark)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        <FormattedNumber value={value} format={fmtUsd} fallback={fallback} />{partial && !loading && <span style={{ marginLeft: 4, fontSize: T.fsXs, color: "var(--v2-amber)", fontWeight: T.fwBold }}>partial</span>}
      </div>
    </div>
  );
}

function ConcessionsColumn({ concessions, partial, loading }) {
  const values = [
    concessions?.abatement,
    concessions?.tiAllowance,
    concessions?.plannedRecovery,
    concessions?.freeRent,
  ];
  const knownValues = values.filter((value) => value != null && Number.isFinite(Number(value)));
  const total = knownValues.length ? knownValues.reduce((sum, value) => sum + Number(value), 0) : null;
  const partialByItem = partial ?? {};
  const anyPartial = values.some((value, index) => (
    value == null || Boolean(partialByItem[["abatement", "tiAllowance", "plannedRecovery", "freeRent"][index]])
  ));
  const totalFallback = loading ? "Loading…" : "Not available";
  return (
    <div data-testid="v2-lease-exposure-concessions">
      <SlabHeading>Concessions &amp; recovery</SlabHeading>
      <div data-testid="v2-lease-exposure-concessions-total" data-partial={anyPartial ? "true" : "false"} style={{ marginBottom: 9 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <strong data-testid="v2-lease-exposure-concessions-total-value" style={{ fontSize: T.fsXl, fontWeight: T.fwDisplay, color: "var(--v2-green)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
            {total == null ? totalFallback : <CountUpNumber value={total} format={fmtUsd} fallback={totalFallback} />}
          </strong>
          {anyPartial && !loading && <span data-testid="v2-lease-exposure-concessions-total-partial" style={{ fontSize: T.fsXs, color: "var(--v2-amber)", fontWeight: T.fwBold }}>partial</span>}
        </div>
        <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 3 }}>credits, allowance &amp; income in your favor</div>
      </div>
      <ConcessionRow label="Abatement remaining" value={concessions?.abatement} loading={loading} partial={Boolean(partialByItem.abatement)} />
      <ConcessionRow label="TI allowance unused" value={concessions?.tiAllowance} loading={loading} partial={Boolean(partialByItem.tiAllowance)} />
      <ConcessionRow label="Planned sublease recovery" value={concessions?.plannedRecovery} loading={loading} partial={Boolean(partialByItem.plannedRecovery)} highlight />
      <ConcessionRow label="Free rent remaining" value={concessions?.freeRent} loading={loading} partial={Boolean(partialByItem.freeRent)} />
    </div>
  );
}

function CostLeakageColumn({ leakage, leakageLoading }) {
  const summary = leakage?.summary;
  const splitTotal = summary
    ? Number(summary.recoverableAnnual) + Number(summary.committedAnnual)
    : 0;
  const recoverablePct = splitTotal > 0 ? Number(summary.recoverableAnnual) / splitTotal * 100 : 0;
  const committedPct = splitTotal > 0 ? Number(summary.committedAnnual) / splitTotal * 100 : 0;
  const unavailable = leakageLoading ? "Loading…" : "Not available";
  const rollOffYear = summary?.latestRollOffYear;
  const deadRentLabel = rollOffYear == null
    ? "dead rent"
    : `dead rent · rolls off by ${rollOffYear}`;
  return (
    <div data-testid="v2-lease-exposure-cost-leakage">
      <SlabHeading>Cost leakage</SlabHeading>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6, marginBottom: 9 }}>
        <div data-testid="v2-lease-exposure-dead-rent" style={{ fontSize: T.fsXl, fontWeight: T.fwDisplay, color: "var(--v2-red)", whiteSpace: "nowrap" }}>
          {summary ? <CountUpNumber value={summary.deadRentAnnual} format={reportUsd} fallback={reportUsd(summary.deadRentAnnual)} /> : unavailable}<span style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginLeft: 3, fontWeight: T.fwBold }}>/yr</span>
        </div>
        <div data-testid="v2-lease-exposure-rolloff" style={{ fontSize: T.fsXs, color: "var(--v2-mid)", textAlign: "right" }}>
          {deadRentLabel}
        </div>
      </div>
      <div data-testid="v2-lease-exposure-leakage-split" role="img" aria-label={summary ? `${fmtPct(recoverablePct)} recoverable, ${fmtPct(committedPct)} committed` : "Cost Leakage split not available"} style={{ display: "flex", height: 7, borderRadius: 99, overflow: "hidden", background: "var(--v2-hair)", margin: "4px 0 6px" }}>
        {summary && <><span style={{ width: `${recoverablePct}%`, background: "var(--v2-amber)" }} /><span style={{ width: `${committedPct}%`, background: "var(--v2-red)" }} /></>}
      </div>
      <div data-testid="v2-lease-exposure-leakage-legend" style={{ display: "flex", justifyContent: "space-between", gap: 6, fontSize: T.fsXs, color: "var(--v2-mid)" }}>
        <span><span data-testid="v2-lease-exposure-leakage-recoverable">{summary ? <FormattedNumber value={summary.recoverableAnnual} format={reportUsd} fallback={reportUsd(summary.recoverableAnnual)} /> : unavailable}</span> recoverable</span>
        <span><span data-testid="v2-lease-exposure-leakage-committed">{summary ? <FormattedNumber value={summary.committedAnnual} format={reportUsd} fallback={reportUsd(summary.committedAnnual)} /> : unavailable}</span> committed</span>
      </div>
      <div data-testid="v2-lease-exposure-leaking-contributors" style={{ marginTop: 14 }}>
        <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid, marginBottom: 4 }}>Top leaking leases</div>
        {summary?.topContributors?.length ? summary.topContributors.map((lease, index) => (
          <div key={lease.leaseId || `${lease.locationId}-${index}`} data-testid={`v2-lease-exposure-leaking-lease-${lease.leaseId || lease.locationId}`} style={{ display: "flex", alignItems: "baseline", gap: 6, padding: "4px 0", borderTop: "1px solid var(--v2-hair)", fontSize: T.fsXs }}>
            <strong style={{ color: "var(--v2-dark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lease.office || lease.lease}</strong>
            <span style={{ color: "var(--v2-mid)" }}>· {lease.source}</span>
            <strong style={{ marginLeft: "auto", whiteSpace: "nowrap", color: "var(--v2-dark)", fontVariantNumeric: "tabular-nums" }}>
              <FormattedNumber value={lease.annualCost} format={reportUsd} fallback={reportUsd(lease.annualCost)} />
            </strong>
          </div>
        )) : <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>{summary ? "No dead-rent contributors" : unavailable}</div>}
      </div>
      <div data-testid="v2-lease-exposure-leakage-identified" style={{ marginTop: 13, fontSize: T.fsXs, color: "var(--v2-mid)" }}>
        <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid, marginBottom: 4 }}>Also identified</div>
        <div>+ {summary ? <FormattedNumber value={summary.underUseAnnual} format={reportUsd} fallback={reportUsd(summary.underUseAnnual)} /> : unavailable} under-use (modeled, not dead rent)</div>
        <div style={{ marginTop: 3 }}><strong style={{ color: "var(--v2-dark)" }}>{summary ? <FormattedNumber value={summary.totalIdentifiedAnnual} format={reportUsd} fallback={reportUsd(summary.totalIdentifiedAnnual)} /> : unavailable}</strong> total identified leakage</div>
      </div>
      <a data-testid="v2-lease-exposure-cost-leakage-link" href="/reports/cost-leakage" style={{ ...OUTBOUND_LINK_STYLE, display: "block", textDecoration: "none", marginTop: 11, textAlign: "right" }}>
        Open Cost Leakage <span aria-hidden="true" style={{ fontSize: T.fsBase, marginLeft: 2 }}>→</span>
      </a>
    </div>
  );
}

function LeaseExposureDetail({ exposure, basisCaption, basis }) {
  const detailRate = basis.rate;
  const detailScopeType = "all";
  const detailScopeValue = null;
  const locationKey = "all:";
  const [detailByLocation, setDetailByLocation] = useState({});
  const [lastSuccessfulDetailByBasis, setLastSuccessfulDetailByBasis] = useState({});
  const [detailFetchErrorKey, setDetailFetchErrorKey] = useState(null);
  const [leakage, setLeakage] = useState(null);
  const [leakageLoading, setLeakageLoading] = useState(true);
  const cachedDetailForLocation = detailByLocation[locationKey];
  const lastSuccessfulDetail = lastSuccessfulDetailByBasis[basis.scope] ?? null;
  const detailExposure = cachedDetailForLocation
    ? cachedDetailForLocation[basis.scope] ?? null
    : lastSuccessfulDetail;
  const detailLoading = !cachedDetailForLocation;

  useEffect(() => {
    if (
      cachedDetailForLocation
      && Object.prototype.hasOwnProperty.call(cachedDetailForLocation, "fully_loaded")
      && Object.prototype.hasOwnProperty.call(cachedDetailForLocation, "base")
    ) return undefined;
    let active = true;
    setDetailFetchErrorKey((current) => current === locationKey ? null : current);
    Promise.all(["fully_loaded", "base"].map((scope) => getPulseLeaseExposure({
      scope,
      rate: detailRate,
      scopeType: detailScopeType,
      scopeValue: detailScopeValue,
      includeDetail: true,
    })))
      .then(([fullyLoadedData, baseData]) => {
        if (!active) return;
        const byBasis = {
          fully_loaded: fullyLoadedData?.exposure ?? null,
          base: baseData?.exposure ?? null,
        };
        setDetailByLocation((current) => ({ ...current, [locationKey]: byBasis }));
        setLastSuccessfulDetailByBasis((current) => ({ ...current, ...byBasis }));
        setDetailFetchErrorKey(null);
      })
      .catch(() => {
        if (active) {
          setDetailFetchErrorKey(locationKey);
        }
      });
    return () => { active = false; };
  }, [detailRate, detailScopeType, detailScopeValue, locationKey, cachedDetailForLocation]);

  useEffect(() => {
    let active = true;
    setLeakageLoading(true);
    getCostLeakageReport()
      .then((report) => {
        if (active) setLeakage(report?.financialsVisible === true && report?.summary ? report : null);
      })
      .catch(() => {
        if (active) setLeakage(null);
      })
      .finally(() => {
        if (active) setLeakageLoading(false);
      });
    return () => { active = false; };
  }, []);

  const detailUpdateMessage = !cachedDetailForLocation && lastSuccessfulDetail
    ? detailFetchErrorKey === locationKey
      ? "Couldn’t refresh location details; showing previous values."
      : "Updating location details; showing previous values."
    : null;

  return (
    <motion.div
      key="lease-exposure-expanded"
      data-testid="v2-lease-exposure-expanded"
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.22, ease: "easeInOut" }}
      style={{ overflow: "hidden", marginTop: 8 }}
    >
      <div style={{ paddingTop: 12, borderTop: "1px solid var(--v2-hair)" }}>
        {detailUpdateMessage && (
          <div
            data-testid="v2-lease-exposure-detail-update"
            role="status"
            style={{ color: "var(--v2-mid)", fontSize: T.fsXs, marginBottom: 6 }}
          >
            {detailUpdateMessage}
          </div>
        )}
        <div data-testid="v2-lease-exposure-detail-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
          <ConcentrationDrivers
            markets={(detailExposure ?? exposure)?.markets ?? []}
            exposure={detailExposure ?? exposure}
          />
          <ConcessionsColumn
            concessions={detailExposure?.concessions ?? exposure.concessions}
            partial={detailExposure?.concessionsPartial ?? exposure.concessionsPartial}
            loading={detailLoading && !detailExposure && !exposure.concessions}
          />
          <CostLeakageColumn leakage={leakage} leakageLoading={leakageLoading} />
        </div>
        <div style={{ marginTop: 10, fontSize: T.fsXs, color: "var(--v2-light)" }}>
          Converted to USD once at latest available FX rate · {basisCaption.toLowerCase()} basis.
        </div>
      </div>
    </motion.div>
  );
}

function LeaseExposureDetailLoading() {
  return (
    <div
      data-testid="v2-lease-exposure-detail-loading"
      role="status"
      style={{ padding: "14px 0", color: "var(--v2-mid)", fontSize: T.fsXs }}
    >
      Loading portfolio-wide detail…
    </div>
  );
}

export function LeaseExposurePane({ onNavigate, renderDetailOutside = false, onVisibilityChange, locationScopeData }) {
  const { basis: fxBasis, liveFetchedAt: fxLiveFetchedAt } = useFxConversionBasis();
  const [scope, setScope] = useState("base");
  const rate = "effective";
  const [locationScope, setLocationScope] = useState({ type: "all", value: null });
  const locationScopeKey = `${locationScope.type}:${locationScope.value ?? ""}`;
  const [exposureByLocation, setExposureByLocation] = useState({});
  const [lastSuccessfulLocationScope, setLastSuccessfulLocationScope] = useState(null);
  const [locationFetchErrorKey, setLocationFetchErrorKey] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const cachedExposureForLocation = exposureByLocation[locationScopeKey];
  const hasCachedLocation = cachedExposureForLocation
    && Object.prototype.hasOwnProperty.call(cachedExposureForLocation, "fully_loaded")
    && Object.prototype.hasOwnProperty.call(cachedExposureForLocation, "base");
  const hasCachedExposure = cachedExposureForLocation
    && Object.prototype.hasOwnProperty.call(cachedExposureForLocation, scope);
  const selectedExposure = hasCachedExposure ? cachedExposureForLocation[scope] : null;
  const displayedLocationScope = hasCachedLocation
    ? locationScope
    : (lastSuccessfulLocationScope ?? locationScope);
  const displayedLocationScopeKey = `${displayedLocationScope.type}:${displayedLocationScope.value ?? ""}`;
  const displayedExposureForLocation = exposureByLocation[displayedLocationScopeKey];
  const hasDisplayedLocation = displayedExposureForLocation
    && Object.prototype.hasOwnProperty.call(displayedExposureForLocation, "fully_loaded")
    && Object.prototype.hasOwnProperty.call(displayedExposureForLocation, "base");
  const exposure = hasDisplayedLocation ? displayedExposureForLocation[scope] : null;
  const portfolioExposure = exposureByLocation["all:"]?.[scope] ?? null;
  const isShowingPreviousLocation = displayedLocationScopeKey !== locationScopeKey;

  useEffect(() => {
    if (hasCachedLocation) return undefined;
    let cancelled = false;
    setLocationFetchErrorKey((current) => current === locationScopeKey ? null : current);
    const locationOptions = locationScope.type !== "all"
      ? { scopeType: locationScope.type, scopeValue: locationScope.value }
      : {};
    Promise.all(["fully_loaded", "base"].map((basisScope) => getPulseLeaseExposure({
      scope: basisScope,
      rate,
      ...locationOptions,
    })))
      .then(([fullyLoadedData, baseData]) => {
        if (cancelled) return;
        setExposureByLocation((current) => ({
          ...current,
          [locationScopeKey]: {
            fully_loaded: fullyLoadedData?.exposure ?? null,
            base: baseData?.exposure ?? null,
          },
        }));
        setLastSuccessfulLocationScope(locationScope);
        setLocationFetchErrorKey(null);
      })
      .catch(() => {
        if (cancelled) return;
        setLocationFetchErrorKey(locationScopeKey);
        // Preserve a working pane during location revalidation. Only hide it
        // when the initial request fails before any location has succeeded.
        if (!lastSuccessfulLocationScope) {
          setForbidden(true);
          onVisibilityChange?.(false);
        }
      });
    return () => { cancelled = true; };
  }, [rate, locationScope, locationScope.type, locationScope.value, locationScopeKey, onVisibilityChange, hasCachedLocation, lastSuccessfulLocationScope]);

  if (forbidden) return null;

  const scopeLabel = SCOPE_OPTIONS.find((o) => o.key === scope)?.label ?? scope;
  const rateLabel = "Effective";
  const getLocationLabel = (scopeSelection, scopeExposure) => {
    const selectedRegion = scopeSelection.type === "region"
      ? locationScopeData?.scopes?.regions?.find((region) => region.value === scopeSelection.value)
      : null;
    const selectedMarket = scopeSelection.type === "market"
      ? locationScopeData?.scopes?.markets?.find((market) => (
        (market.scopeValue ?? market.marketId ?? `${market.region}::${market.value}`) === scopeSelection.value
      ))
      : null;
    const duplicateMarketName = selectedMarket
      && (locationScopeData?.scopes?.markets ?? []).filter((market) => market.value === selectedMarket.value).length > 1;
    const selectedOffice = scopeSelection.type === "office"
      ? locationScopeData?.scopes?.offices?.find((office) => office.value === scopeSelection.value)
      : null;
    const locationLabel = scopeSelection.type === "all"
      ? "All locations"
      : selectedRegion
        ? `${selectedRegion.label} · ${selectedRegion.officeCount} offices`
        : selectedMarket
          ? `${selectedMarket.label}${duplicateMarketName ? ` · ${selectedMarket.region}` : ""} · ${selectedMarket.officeCount} offices`
          : selectedOffice
            ? `${selectedOffice.market ? `${selectedOffice.market} · ` : ""}${selectedOffice.label}${scopeExposure?.leaseCount == null ? "" : ` · ${scopeExposure.leaseCount} lease${scopeExposure.leaseCount === 1 ? "" : "s"}`}`
            : scopeSelection.value ?? "All locations";
    return { locationLabel, selectedOffice };
  };
  const { locationLabel } = getLocationLabel(displayedLocationScope, exposure);
  const { locationLabel: selectedLocationLabel, selectedOffice } = getLocationLabel(locationScope, selectedExposure);
  const pickerLocationLabel = selectedOffice
    ? <><OfficeLocationLabel office={selectedOffice} />{selectedExposure?.leaseCount == null ? "" : ` · ${selectedExposure.leaseCount} lease${selectedExposure.leaseCount === 1 ? "" : "s"}`}</>
    : selectedLocationLabel;
  const isEmptyScopedExposure = exposure?.hasQualifyingActiveLease === false;
  const basisCaption = `${scopeLabel} · ${rateLabel} rate`;
  const locationUpdateMessage = isShowingPreviousLocation
    ? locationFetchErrorKey === locationScopeKey
      ? `Couldn’t load ${selectedLocationLabel}; showing ${locationLabel}.`
      : `Updating to ${selectedLocationLabel}; showing ${locationLabel}.`
    : null;

  const tooltipText = `Total committed lease exposure aggregated from rent, opex, and capex schedules across every active lease, converted to USD once. Toggle Scope/Rate to change what counts. FX basis: ${formatFxBasisSummary(fxBasis, fxLiveFetchedAt)}.`;

  const exposureSummary = (
    <div
      className={renderDetailOutside ? "v2-lease-exposure-summary" : undefined}
      data-testid="v2-section-lease-exposure"
      style={{
      background: "var(--v2-card)", border: "1px solid var(--v2-line)",
      borderRadius: 16, boxShadow: "var(--v2-shadow-card)", padding: "18px 18px 14px",
      height: renderDetailOutside ? "100%" : (expanded ? "auto" : PULSE_PANE_H),
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      containerType: "inline-size",
      containerName: "lease-exposure",
    }}
    >
      {/* Header — the location scope is local to this pane; rate toggle lives in More. */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap", flexShrink: 0 }}>
        <div data-testid="v2-lease-exposure-title" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsBase, fontWeight: T.fwBold }}>
          <span style={{ display: "inline-block", width: 3, height: 14, background: "var(--v2-accent)", borderRadius: 2 }} />
          Lease exposure
          <PaneInfoTooltip
            testId="v2-lease-exposure-tooltip"
            label="Lease exposure methodology and FX basis"
            text={tooltipText}
          />
        </div>
        <LocationScopePicker
          data={locationScopeData}
          scopeType={locationScope.type}
          scopeValue={locationScope.value}
          selectedLabel={pickerLocationLabel}
          onSelect={(type, value) => {
            if (expanded && type !== "all") setExpanded(false);
            setLocationScope({ type, value });
          }}
          testIdPrefix="lease-exposure"
           includeGroups
           stableMarketIdentity
        />
      </div>
      {locationUpdateMessage && (
        <div
          data-testid="v2-lease-exposure-location-update"
          role="status"
          style={{ color: "var(--v2-mid)", fontSize: T.fsXs, marginBottom: 6 }}
        >
          {locationUpdateMessage}
        </div>
      )}

      {!hasDisplayedLocation || !exposure ? (
        <div style={{ display: "grid", placeItems: "center", flex: 1, color: "var(--v2-light)", fontSize: T.fsLg }}>
          {!hasDisplayedLocation ? "Loading…" : "No lease exposure data"}
        </div>
      ) : isEmptyScopedExposure ? (
        <div data-testid="v2-lease-exposure-empty" style={{ display: "grid", placeItems: "center", flex: 1, minHeight: 220, border: "1px dashed var(--v2-line)", borderRadius: 10, color: "var(--v2-mid)", textAlign: "center", padding: 20 }}>
          <div>
            <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>No active lease on file</div>
             <div style={{ marginTop: 6, fontSize: T.fsBase }}>This location scope has no qualifying active lease data for exposure.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Two-column body: total+tiles left, chart right — left column top-aligns, chart sizes to its own content */}
          <div className="v2-lease-exposure-body" data-testid="v2-lease-exposure-body">
            {/* Left column: total block on top + 3-across horizon tiles centered below */}
            <div className="v2-lease-exposure-metrics" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", minWidth: 0 }}>
              <TotalBlock
                value={exposure.totals.grossUsd}
                leaseCount={exposure.leaseCount}
                scopeLabel={`${locationLabel} · ${scopeLabel}`}
                rateLabel={rateLabel}
                scope={scope}
                onScopeChange={setScope}
              />
              {/* Centering wrapper — takes remaining vertical space, centers tiles within it */}
              <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                {/* 3-across horizon tiles — sized to content, not stretched to chart height */}
                <div className="v2-lease-exposure-horizons" style={{
                  display: "grid",
                  gap: 10,
                  width: "100%",
                }}>
                  <MetricCell
                    testId="v2-lease-exposure-horizon-12mo"
                    label="Next 12mo"
                    value={exposure.horizon.next12moUsd}
                    tooltip={HORIZON_TOOLTIP}
                  />
                  <MetricCell
                    testId="v2-lease-exposure-horizon-3yr"
                    label="Next 3yr"
                    value={exposure.horizon.next3yrUsd}
                    tooltip={HORIZON_TOOLTIP}
                  />
                  <MetricCell
                    testId="v2-lease-exposure-horizon-5yr"
                    label="Next 5yr"
                    value={exposure.horizon.next5yrUsd}
                    tooltip={HORIZON_TOOLTIP}
                  />
                </div>
              </div>
              <StrategySplitBar split={exposure.strategySplit} />
            </div>

            {/* Right column: by-year chart — fills column height */}
            <ByYearBarChart years={exposure.byYear} resetKey={`${scope}:${locationScope.type}:${locationScope.value}`} />
          </div>

          {/* Footer — full-width: decision hook + "More ▾" on same row, above the divider */}
          <div style={{
            borderBottom: "1px solid var(--v2-hair)",
            paddingBottom: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            flexWrap: "wrap",
            gap: 8,
          }}>
            <button
              data-testid="v2-lease-exposure-decision-line"
              onClick={() => {
                if (!onNavigate) return;
                const params = new URLSearchParams({
                  view: "leases",
                  leaseView: "timeline",
                  decisionScope: LEASE_DECISION_WINDOW_SCOPE,
                });
                for (const leaseId of exposure.decisionWindow.leaseIds ?? []) {
                  params.append("decisionLeaseId", leaseId);
                }
                onNavigate(`?${params.toString()}`);
              }}
              style={{
                background: "none", border: "none", padding: 0, cursor: "pointer",
                fontSize: T.fsBase, fontWeight: T.fwSemibold, color: "var(--v2-dark)",
                textAlign: "left",
              }}
            >
              {fmtUsd(exposure.decisionWindow.totalUsd)} expires or breaks within {exposure.decisionWindow.months} months · <span style={{ color: "var(--v2-amber)" }}>{exposure.decisionWindow.count} decision{exposure.decisionWindow.count === 1 ? "" : "s"}</span>{" "}
              <span style={{ color: "var(--v2-amber)" }}>→</span>
            </button>
            <button
              data-testid="v2-lease-exposure-expand-toggle"
              onClick={() => {
                if (expanded) {
                  setExpanded(false);
                  return;
                }
                setLocationScope({ type: "all", value: null });
                setExpanded(true);
              }}
              style={{
                ...OUTBOUND_LINK_STYLE,
                whiteSpace: "nowrap",
                marginLeft: 12,
              }}
            >
              {expanded ? "Less ▲" : "More ▾"}
            </button>
          </div>

          {!renderDetailOutside && (
            <AnimatePresence initial={false}>
              {expanded && (
                portfolioExposure
                  ? <LeaseExposureDetail
                      exposure={portfolioExposure}
                      basisCaption={basisCaption}
                      basis={{ scope, rate }}
                    />
                  : <LeaseExposureDetailLoading />
              )}
            </AnimatePresence>
          )}
        </>
      )}
    </div>
  );

  if (!renderDetailOutside) return exposureSummary;

  return (
    <>
      {exposureSummary}
      <AnimatePresence initial={false}>
        {expanded && (exposure || isShowingPreviousLocation) && portfolioExposure?.hasQualifyingActiveLease !== false && (
          <div className="v2-lease-exposure-detail-card">
            {portfolioExposure
              ? <LeaseExposureDetail
                  exposure={portfolioExposure}
                  basisCaption={basisCaption}
                  basis={{ scope, rate }}
                />
              : <LeaseExposureDetailLoading />}
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
